/*
クラウドエディターの環境の種類の表。正本はここ 1 か所。

- 画面 (手順の板の章の種類、空き具合) はここを読む
- gateway (Go) はここから書き出した tools/sandbox/gateway/kinds.json を組み込みで読む。
  書き出しは bun run cloudeditor:kinds。JSON は手で直さない (試験が食い違いを落とす)
- VPS の SBX_KINDS は setup.sh が kinds.json から作る

種類で変わるのは、メモリと CPU の上限と、使う言語の補助 (VS Code の拡張機能と言語サーバー) と、PostgreSQL を起こす時機だけ。
イメージは 1 つで、道具は全部入っている。ホームは種類をまたいで同じ人のもの 1 つ。
種類を細かく分けるのは、使う量が違うときだけにする (同じ量なら表示の名前が増えるだけで、人数は増えない)。
*/

/**
 * 言語の補助の組。イメージには全部入れてあり、種類に無い組は Pod を作るときに止める (sandbox-ide が
 * VS Code の無効の一覧に書く)。ID は Open VSX か VS Code に組み込みの拡張機能の ID (英小文字)。
 */
export const LANG_HELPERS = {
  python: ["ms-python.python", "ms-python.debugpy"],
  java: ["redhat.java", "vscjava.vscode-java-debug"],
  js: ["vscode.typescript-language-features", "dbaeumer.vscode-eslint"],
  go: ["golang.go"],
} as const;

export type LangHelper = keyof typeof LANG_HELPERS;

export interface SandboxKind {
  /** gateway の種類の名前。英小文字 2〜12 文字 */
  name: string;
  /** 画面に出す名前 */
  label: string;
  /** 何を学ぶ種類か (1 行) */
  summary: string;
  /** Pod のメモリの上限 (limits。docker の --memory の形。512m、2g)。超えると落ちる */
  memory: string;
  /**
   * ふだんの量 (Pod のメモリの requests)。書かなければ上限と同じ (Guaranteed)。
   * 上限より小さくすると Burstable になり、k3s の道の満員 (gateway と ResourceQuota) はこの値で数える。
   * 変えたら tools/sandbox/capacity.py で quota と人数を引き直す。256MiB 以上
   */
  memoryRequest?: string;
  /** Pod の CPU の requests (0.5 や 1)。起動の山は gateway の SBX_CPU_BURST まで使える */
  cpus: string;
  langHelpers: LangHelper[];
  /**
   * PostgreSQL をいつ起こすか。start は環境を作ったときに裏で起こす (SQL の章と、TODO アプリなど DB を使う実践プロジェクト)。
   * lazy は起こさず、psql を初めて打ったときに起こす (1 人 40MiB ほどと起動の CPU を、使わない種類で払わない)
   */
  database: "start" | "lazy";
  /** 種類の頭に出す技術のロゴ (apps/client/public 以下のパス。devicon 2.16.0、MIT)。装飾なので alt は付けない */
  logo: string;
}

/**
 * 上限は、VS Code を開いて種類のよくある作業を流したときの山 (tools/sandbox/measure-open.mjs --work) に余白を足した値。
 * 測った値は tools/sandbox/README.md の「開くまでの速さ」と「環境の種類」。
 *
 * 上限 (memory) は種類の重い作業 (measure-open.mjs --work。pandas の venv、30 万行の JOIN など) の山から決める。
 * ふだんの量 (memoryRequest) は学習パスの章を開いて章の作業をしたあと (--work --lesson、2 回目に開いた人) の量の
 * 中央値の 1.1 倍を 32MiB の倍に切り上げた値。上限は山、ふだんの量はふだん、で分ける (2026-10-10 の値も軽い作業から引いていた)。
 * 2026-10-11 の測定 (VPS の検証用の Pod、2 回ずつ) linux 370 (404 と 336) → 416、web 424 → 480、db 476 (前後の 4 回。db は
 * 中身を変えていない) → 544、python 500 → 576。cc は測っておらず前の値のまま。node 778、java 990、next 753、go 994 は前の測定。
 * k3s の道は満員をこの値で数える (Burstable)。人数は tools/sandbox/capacity.py がこの表 (kinds.json) から引き、
 * k8s/20-quota.yaml と gateway の SBX_MEMORY_BUDGET と SBX_MAX_CONTAINERS をそれに合わせる。
 * 全員が同時に上限まで使うと足りず、kubelet が requests より多く使っている人から追い出す (画面には理由が出る)
 *
 * 2026-10-20 の本番の通し (ブラウザで学習パス 71〜86 の SQL の章を 3 アカウントで続けて開く) で、db の Pod が上限 (640m) の
 * OOM で落ち (dmesg の CONSTRAINT_MEMCG、gvisor_sentry)、「メモリが足りず環境が止まりました」が 11 章で出た。java も 1 章で落ち、
 * 動いている Pod の memory.peak は上限 (1536) に張り付いていた。章を次々に開くと閉じたタブの分 (30 秒の猶予) と gVisor の中の
 * ページキャッシュが重なり、1 章ずつ測った山 (db 557、java 1117) より高くなる。db は 768m (10/10 に 640m へ下げる前の値) へ、
 * java は 1792m へ戻した。ふだんの量は 32MiB ずつだけ上げ、人数 (db 7、java 3) と quota の 4096Mi は変えていない
 */
export const KINDS = [
  { name: "linux", label: "Linux", summary: "ターミナルとファイル、シェル", memory: "512m", memoryRequest: "416m", cpus: "0.5", langHelpers: [], database: "lazy", logo: "/tech-logos/linux.svg" },
  { name: "web", label: "Web ページ", summary: "HTML、CSS、素の JavaScript (プレビューで見る)", memory: "512m", memoryRequest: "480m", cpus: "0.5", langHelpers: [], database: "lazy", logo: "/tech-logos/web.svg" },
  { name: "cc", label: "C 言語", summary: "gcc でコンパイルして動かす", memory: "512m", memoryRequest: "448m", cpus: "0.5", langHelpers: [], database: "lazy", logo: "/tech-logos/cc.svg" },
  { name: "db", label: "SQL と PostgreSQL", summary: "psql で SQL を書く、テーブルを設計する", memory: "768m", memoryRequest: "576m", cpus: "0.5", langHelpers: [], database: "start", logo: "/tech-logos/db.svg" },
  { name: "python", label: "Python", summary: "Python、pip、Flask", memory: "1g", memoryRequest: "576m", cpus: "0.5", langHelpers: ["python"], database: "lazy", logo: "/tech-logos/python.svg" },
  { name: "go", label: "Go", summary: "Go で書いて動かす、go run と go test", memory: "2g", memoryRequest: "1120m", cpus: "1", langHelpers: ["go"], database: "start", logo: "/tech-logos/go.svg" },
  { name: "node", label: "Node.js と React", summary: "TypeScript、React (Vite)、Express", memory: "1536m", memoryRequest: "864m", cpus: "1", langHelpers: ["js"], database: "start", logo: "/tech-logos/node.svg" },
  { name: "java", label: "Java", summary: "JDK 25、Maven、Spring Boot", memory: "1792m", memoryRequest: "1152m", cpus: "1", langHelpers: ["java"], database: "start", logo: "/tech-logos/java.svg" },
  { name: "next", label: "Next.js", summary: "Next.js の dev と build", memory: "2g", memoryRequest: "832m", cpus: "1", langHelpers: ["js"], database: "start", logo: "/tech-logos/next.svg" },
] as const satisfies readonly SandboxKind[];

export type KindName = (typeof KINDS)[number]["name"];

/** 端末だけの入口と、章を選ぶ前の既定の種類 (gateway の SBX_DEFAULT_KIND と同じ) */
export const DEFAULT_KIND: KindName = "linux";

export function kindOf(name: string): SandboxKind | undefined {
  return KINDS.find((kind) => kind.name === name);
}

/** gateway が組み込みで読む JSON (tools/sandbox/gateway/kinds.json) */
export function kindsJson(): string {
  return `${JSON.stringify({
    langHelpers: LANG_HELPERS,
    kinds: KINDS.map((kind: SandboxKind) => ({
      name: kind.name,
      label: kind.label,
      memory: kind.memory,
      memoryRequest: kind.memoryRequest ?? kind.memory,
      cpus: kind.cpus,
      langHelpers: kind.langHelpers,
      database: kind.database,
    })),
  }, null, 2)}\n`;
}
